/**
 * chart.js plugin drawing landmarks on the line charts: a tinted band between
 * two x values (a PRF, the reservoir) or a dotted marker at one x value (a
 * confluence). Items are in x-axis units, so the same plugin serves the
 * categorical panels (indices) and the stream-mile profile (miles).
 */

const STYLE = {
  prf: { fill: "rgba(46, 125, 50, 0.10)", stroke: "#2e7d32" },
  reservoir: { fill: "rgba(2, 119, 189, 0.10)", stroke: "#0277bd" },
  confluence: { fill: "rgba(109, 76, 65, 0.10)", stroke: "#6d4c41" },
  outfall: { fill: "rgba(198, 40, 40, 0.08)", stroke: "#c62828" },
  other: { fill: "rgba(0, 0, 0, 0.06)", stroke: "#546e7a" },
};

export const bandsPlugin = {
  id: "usdsBands",
  beforeDatasetsDraw(chart, _args, opts) {
    const items = opts?.items ?? [];
    const x = chart.scales?.x;
    if (!items.length || !x) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.font = "600 11px sans-serif";
    ctx.textBaseline = "top";
    items.forEach((item) => {
      const style = STYLE[item.kind] ?? STYLE.other;
      if (item.x1 != null && item.x2 != null) {
        const p1 = x.getPixelForValue(item.x1);
        const p2 = x.getPixelForValue(item.x2);
        const left = Math.max(chartArea.left, Math.min(p1, p2));
        const right = Math.min(chartArea.right, Math.max(p1, p2));
        if (right <= left) return;
        ctx.fillStyle = style.fill;
        ctx.fillRect(
          left,
          chartArea.top,
          right - left,
          chartArea.bottom - chartArea.top
        );
        ctx.strokeStyle = style.stroke;
        ctx.globalAlpha = 0.5;
        ctx.setLineDash([2, 4]);
        [left, right].forEach((p) => {
          ctx.beginPath();
          ctx.moveTo(p, chartArea.top);
          ctx.lineTo(p, chartArea.bottom);
          ctx.stroke();
        });
        ctx.globalAlpha = 1;
        ctx.setLineDash([]);
        ctx.fillStyle = style.stroke;
        ctx.textAlign = "center";
        ctx.fillText(item.label, (left + right) / 2, chartArea.top + 4);
      } else if (item.x != null) {
        const p = x.getPixelForValue(item.x);
        if (p < chartArea.left || p > chartArea.right) return;
        ctx.strokeStyle = style.stroke;
        ctx.globalAlpha = 0.7;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(p, chartArea.top);
        ctx.lineTo(p, chartArea.bottom);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.setLineDash([]);
        ctx.fillStyle = style.stroke;
        ctx.textAlign = "left";
        ctx.fillText(
          item.label,
          p + 4,
          chartArea.top + 4 + (item.row ?? 0) * 14
        );
      }
    });
    ctx.restore();
  },
};

/**
 * Landmarks to plugin items.
 * @param {array} landmarks landmarksForReach output
 * @param {"profile"|"category"} mode miles, or category indices
 * @param {string[]} labels the category labels, for "category" mode
 */
export const toBandItems = (landmarks, mode, labels = []) => {
  const idx = (name) => labels.indexOf(name);
  const items = [];
  let confluenceRow = 0;
  (landmarks ?? []).forEach((lm) => {
    if (lm.kind === "confluence") {
      const x =
        mode === "profile"
          ? lm.mile
          : idx(lm.afterLocationName) >= 0 && idx(lm.beforeLocationName) >= 0
          ? (idx(lm.afterLocationName) + idx(lm.beforeLocationName)) / 2
          : null;
      if (x == null) return;
      /* Band labels take the first row; confluences stack beneath so a mouth
         into the reservoir never prints over the reservoir's own label. */
      items.push({ kind: lm.kind, x, label: lm.label, row: confluenceRow + 1 });
      confluenceRow += 1;
      return;
    }
    const x1 = mode === "profile" ? lm.afterMile : idx(lm.afterLocationName);
    const x2 = mode === "profile" ? lm.beforeMile : idx(lm.beforeLocationName);
    if (x1 == null || x2 == null || x1 < 0 || x2 < 0) return;
    items.push({ kind: lm.kind, x1, x2, label: lm.label });
  });
  return items;
};

/**
 * A dashed vertical guide at the hovered x, so it is plain which site the
 * tooltip is describing when several series share it. Drawn after the data
 * so it sits over the lines.
 */
export const hoverGuidePlugin = {
  id: "usdsHoverGuide",
  afterDatasetsDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.() ?? [];
    if (!active.length) return;
    const x = active[0].element?.x;
    if (!Number.isFinite(x)) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle = "rgba(55, 71, 79, 0.6)";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};
